import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TASK_STATUSES, TASK_STATUS_LABELS, TASK_PRIORITIES, statusBadgeClass, priorityBadgeClass, hasPerm } from "@/lib/constants";
import { Plus, MessageSquare, Clock, User as UserIcon, Trash2, Play, Pause, Square } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useTimer, formatDuration } from "@/contexts/TimerContext";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/Pagination";
import MemberTagSelector from "@/components/MemberTagSelector";

export default function KanbanBoard({ projectId, users, teams, defaultTaskId, q = "", myTasksOnly = false, onCloseDetail }) {
  const { user: me } = useAuth();
  const { timer: activeTimer, elapsed: activeElapsed, start: tStart, pause: tPause, stop: tStop } = useTimer();
  const [open, setOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", team_id: "", priority: "MEDIUM", status: "BACKLOG", due_date: "", estimated_hours: "", tagged_user_ids: [] });
  const [errors, setErrors] = useState({});
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [activity, setActivity] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [subtaskForm, setSubtaskForm] = useState({ title: "", assignee_id: "" });

  const { items: tasks, total, page, pageSize, setPage, setPageSize, refresh: load } = usePaginatedList("/tasks", {
    project_id: projectId,
    q,
    assignee_id: myTasksOnly ? me?.id : ""
  });

  useEffect(() => {
    const handleTimerStopped = () => {
      load();
    };
    window.addEventListener("timer-stopped", handleTimerStopped);
    return () => {
      window.removeEventListener("timer-stopped", handleTimerStopped);
    };
  }, [load]);

  const openedDefaultTaskRef = useRef(null);

  useEffect(() => {
    if (defaultTaskId && openedDefaultTaskRef.current !== defaultTaskId) {
      const fetchDefault = async () => {
        try {
          const { data } = await api.get(`/tasks/${defaultTaskId}`);
          openedDefaultTaskRef.current = defaultTaskId;
          openDetail(data);
        } catch {}
      };
      fetchDefault();
    }
  }, [defaultTaskId]);

  const handleCloseDetail = () => {
    setDetailTask(null);
    setErrors({});
    if (onCloseDetail) {
      onCloseDetail();
    } else {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has("taskId")) {
          url.searchParams.delete("taskId");
          window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
        }
      } catch {}
    }
  };

  const openNew = (status = "BACKLOG") => {
    setForm({ title: "", description: "", assignee_id: "", team_id: "", priority: "MEDIUM", status, due_date: "", estimated_hours: "", tagged_user_ids: [] });
    setErrors({});
    setOpen(true);
  };

  const create = async () => {
    const newErrors = {};
    if (!form.title.trim()) {
      newErrors.title = "Task Title is required";
    }
    if (!form.team_id) {
      newErrors.team_id = "Team is required";
    }
    if (form.estimated_hours) {
      const est = Number(form.estimated_hours);
      if (isNaN(est) || est <= 0) {
        newErrors.estimated_hours = "Estimated hours must be a positive number";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      await api.post("/tasks", {
        ...form, project_id: projectId,
        assignee_id: form.assignee_id || null,
        tagged_user_ids: form.tagged_user_ids || [],
        team_id: form.team_id || null,
        due_date: form.due_date || null,
        estimated_hours: form.estimated_hours ? Number(form.estimated_hours) : null,
      });
      toast.success("Task created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDetail = async (t) => {
    setDetailTask(t);
    setNewComment("");
    setErrors({});
    setSubtasks([]);
    setSubtaskForm({ title: "", assignee_id: "" });
    try {
      const [c, a, s] = await Promise.all([
        api.get(`/tasks/${t.id}/comments`),
        api.get(`/tasks/${t.id}/activity`),
        api.get(`/tasks/${t.id}/subtasks`),
      ]);
      setComments(c.data);
      setActivity(a.data);
      setSubtasks(s.data);
    } catch {}
  };

  const updateTask = async (patch) => {
    if (!detailTask) return;
    try {
      const { data } = await api.put(`/tasks/${detailTask.id}`, patch);
      setDetailTask(data);
      const a = await api.get(`/tasks/${detailTask.id}/activity`);
      setActivity(a.data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const moveStatus = async (task, status) => {
    try {
      await api.put(`/tasks/${task.id}`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addComment = async () => {
    if (!newComment.trim()) {
      setErrors((prev) => ({ ...prev, comment: "Comment content is required" }));
      return;
    }
    setErrors((prev) => ({ ...prev, comment: "" }));
    try {
      await api.post(`/tasks/${detailTask.id}/comments`, { content: newComment });
      setNewComment("");
      const [c, a] = await Promise.all([
        api.get(`/tasks/${detailTask.id}/comments`),
        api.get(`/tasks/${detailTask.id}/activity`),
      ]);
      setComments(c.data); setActivity(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAddSubtask = async () => {
    if (!subtaskForm.title.trim()) {
      setErrors((prev) => ({ ...prev, subtask_title: "Subtask title is required" }));
      return;
    }
    setErrors((prev) => ({ ...prev, subtask_title: "" }));
    try {
      await api.post("/tasks", {
        title: subtaskForm.title,
        description: "",
        project_id: projectId,
        assignee_id: subtaskForm.assignee_id || null,
        priority: "MEDIUM",
        status: "BACKLOG",
        parent_id: detailTask.id,
      });
      setSubtaskForm({ title: "", assignee_id: "" });
      setErrors((prev) => ({ ...prev, subtask_title: "" }));
      toast.success("Subtask added");
      const { data } = await api.get(`/tasks/${detailTask.id}/subtasks`);
      setSubtasks(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (t) => {
    if (!window.confirm("Delete task?")) return;
    try { await api.delete(`/tasks/${t.id}`); toast.success("Deleted"); handleCloseDetail(); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";
  const canCreate = hasPerm(me, "task.create");

  return (
    <div className="space-y-4" data-testid="kanban-board">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Drag-drop is via status dropdown per card (keyboard-friendly).</div>
        {canCreate && <Button onClick={() => openNew()} size="sm" data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" /> New Task</Button>}
      </div>

      <div className="flex overflow-x-auto gap-4 pb-4">
        {TASK_STATUSES.map((status) => {
          const colTasks = tasks.filter((t) => t.status === status);
          return (
            <div key={status} className="min-w-[300px] w-[300px] flex-shrink-0 kanban-col rounded-md p-3" data-testid={`kanban-col-${status}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={statusBadgeClass(status)}>{TASK_STATUS_LABELS[status]}</Badge>
                  <span className="text-xs text-muted-foreground font-mono">{colTasks.length}</span>
                </div>
                {canCreate && (
                  <button onClick={() => openNew(status)} className="text-muted-foreground hover:text-foreground" data-testid={`kanban-add-${status}`}>
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {colTasks.map((t) => (
                  <Card
                    key={t.id}
                    className="p-3 cursor-pointer hover-lift"
                    data-testid={`task-card-${t.id}`}
                    onClick={() => openDetail(t)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium leading-snug flex-1" title={t.title}>
                        {t.title.length > 30 ? t.title.slice(0, 30) + "..." : t.title}
                      </div>
                      <Badge variant="outline" className={`${priorityBadgeClass(t.priority)} text-[9px] shrink-0`}>{t.priority}</Badge>
                    </div>
                    {t.parent_title && (
                      <div className="text-[10px] text-primary/80 font-medium mt-1 truncate" title={`Subtask of: ${t.parent_title}`} data-testid={`task-card-parent-${t.id}`}>
                        Subtask of: {t.parent_title}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                      <UserIcon className="w-3 h-3" />
                      <span className="truncate">{userName(t.assignee_id)}</span>
                    </div>
                    {t.tagged_user_ids && t.tagged_user_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {t.tagged_user_ids.map((uid) => (
                          <Badge key={uid} variant="secondary" className="text-[9px] py-0 px-1 font-normal">
                            @{userName(uid).split(" ")[0]}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {t.due_date && (
                      <div className="flex items-center gap-1 mt-1.5 text-[10px] text-muted-foreground font-mono">
                        <Clock className="w-3 h-3" /> {t.due_date}
                      </div>
                    )}
                  </Card>
                ))}
                {colTasks.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No tasks</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        testidPrefix="kanban-tasks-pagination"
      />

      {/* Create task dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-create-dialog">
          <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => { setForm({ ...form, title: e.target.value }); setErrors((prev) => ({ ...prev, title: "" })); }} data-testid="task-form-title" />
              {errors.title && <span className="text-xs text-red-500 block mt-1">{errors.title}</span>}
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-form-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="task-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assignee</Label>
              <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-form-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Team</Label>
              <Select value={form.team_id || "none"} onValueChange={(v) => { setForm({ ...form, team_id: v === "none" ? "" : v }); setErrors(prev => ({ ...prev, team_id: "" })); }}>
                <SelectTrigger data-testid="task-form-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select team</SelectItem>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.team_id && <span className="text-xs text-red-500 block mt-1">{errors.team_id}</span>}
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-form-due" />
            </div>
            <div>
              <Label>Estimated hours</Label>
              <Input type="number" min="0" step="0.5" value={form.estimated_hours} onChange={(e) => { setForm({ ...form, estimated_hours: e.target.value }); setErrors((prev) => ({ ...prev, estimated_hours: "" })); }} data-testid="task-form-estimate" />
              {errors.estimated_hours && <span className="text-xs text-red-500 block mt-1">{errors.estimated_hours}</span>}
            </div>
            <div>
              <MemberTagSelector
                users={users}
                selectedUserIds={form.tagged_user_ids}
                onChange={(taggedIds) => setForm({ ...form, tagged_user_ids: taggedIds })}
                label="Tag Team Members"
                placeholder="Tag a collaborator..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="task-form-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task detail */}
      <Dialog open={!!detailTask} onOpenChange={(v) => { if (!v) handleCloseDetail(); }}>
        <DialogContent className="max-w-2xl" data-testid="task-detail-dialog">
          {detailTask && (
            <>
              {detailTask.parent_id && (
                <button
                  onClick={async () => {
                    try {
                      const r = await api.get(`/tasks/${detailTask.parent_id}`);
                      openDetail(r.data);
                    } catch {
                      toast.error("Failed to load parent task");
                    }
                  }}
                  className="text-xs text-primary hover:underline flex items-center gap-1 mb-2 self-start"
                  data-testid="back-to-parent-btn"
                >
                  ← Back to parent task
                </button>
              )}
              <DialogHeader>
                <DialogTitle className="pr-8">{detailTask.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={statusBadgeClass(detailTask.status)}>{TASK_STATUS_LABELS[detailTask.status]}</Badge>
                  <Badge variant="outline" className={priorityBadgeClass(detailTask.priority)}>{detailTask.priority}</Badge>
                  <span className="text-xs text-muted-foreground font-mono">#{detailTask.id.slice(-6)}</span>
                  {detailTask.estimated_hours != null && <Badge variant="outline" className="text-[10px]">EST {detailTask.estimated_hours}h</Badge>}
                  {detailTask.total_minutes_tracked > 0 && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">Tracked {Math.round(detailTask.total_minutes_tracked)}m</Badge>}
                  {(detailTask.deadline_changes || 0) > 0 && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">Deadline changed {detailTask.deadline_changes}×</Badge>}
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{detailTask.description || "No description"}</p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Assignee</Label>
                    <Select value={detailTask.assignee_id || "none"} onValueChange={(v) => updateTask({ assignee_id: v === "none" ? null : v })}>
                      <SelectTrigger data-testid="task-detail-assignee"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={detailTask.status} onValueChange={(v) => updateTask({ status: v })}>
                      <SelectTrigger data-testid="task-detail-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <Select value={detailTask.priority} onValueChange={(v) => updateTask({ priority: v })}>
                      <SelectTrigger data-testid="task-detail-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TASK_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Due date</Label>
                    <Input type="date" value={detailTask.due_date || ""} onChange={(e) => updateTask({ due_date: e.target.value || null })} data-testid="task-detail-due" />
                  </div>
                  <div>
                    <Label className="text-xs">Estimated hours</Label>
                    <Input type="number" min="0" step="0.5" value={detailTask.estimated_hours ?? ""} onChange={(e) => updateTask({ estimated_hours: e.target.value ? Number(e.target.value) : null })} data-testid="task-detail-estimate" />
                  </div>
                </div>

                <div className="p-3 border border-border rounded-lg bg-card">
                  <MemberTagSelector
                    users={users}
                    selectedUserIds={detailTask.tagged_user_ids || []}
                    onChange={(newIds) => updateTask({ tagged_user_ids: newIds })}
                    label="Tagged Members"
                    placeholder="Tag another member to this task..."
                  />
                </div>

                {!detailTask.parent_id && (
                  <div>
                    <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">Subtasks</div>
                    <div className="space-y-2 mb-3">
                      {subtasks.map((sub) => (
                        <div
                          key={sub.id}
                          className="flex items-center justify-between p-2 border border-border rounded-md hover:bg-muted/30 cursor-pointer text-sm"
                          onClick={async () => {
                            try {
                              const r = await api.get(`/tasks/${sub.id}`);
                              openDetail(r.data);
                            } catch {}
                          }}
                          data-testid={`subtask-item-${sub.id}`}
                        >
                          <div className="font-medium truncate mr-2">{sub.title}</div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted-foreground">{userName(sub.assignee_id)}</span>
                            <Badge variant="outline" className={statusBadgeClass(sub.status)}>{TASK_STATUS_LABELS[sub.status]}</Badge>
                          </div>
                        </div>
                      ))}
                      {subtasks.length === 0 && <div className="text-xs text-muted-foreground">No subtasks yet</div>}
                    </div>

                    <div className="border border-dashed border-border p-3 rounded-md space-y-2">
                      <div className="text-xs font-semibold text-muted-foreground">Add Subtask</div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Input
                            placeholder="Subtask title…"
                            value={subtaskForm.title}
                            onChange={(e) => { setSubtaskForm({ ...subtaskForm, title: e.target.value }); setErrors((prev) => ({ ...prev, subtask_title: "" })); }}
                            data-testid="subtask-title-input"
                            className="h-8 text-sm w-full"
                          />
                          {errors.subtask_title && <span className="text-xs text-red-500 block mt-1">{errors.subtask_title}</span>}
                        </div>
                        <Select
                          value={subtaskForm.assignee_id || "none"}
                          onValueChange={(v) => setSubtaskForm({ ...subtaskForm, assignee_id: v === "none" ? "" : v })}
                        >
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid="subtask-assignee-select">
                            <SelectValue placeholder="Assignee" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Unassigned</SelectItem>
                            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={handleAddSubtask}
                          size="sm"
                          className="h-8 text-xs"
                          data-testid="subtask-submit-btn"
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">Comments</div>
                  <div className="space-y-2">
                    {comments.map((c) => (
                      <div key={c.id} className="border border-border rounded-md p-2 text-sm">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-xs">{c.user_name}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <div>{c.content}</div>
                      </div>
                    ))}
                    {comments.length === 0 && <div className="text-xs text-muted-foreground">No comments yet</div>}
                  </div>
                  <div className="flex flex-col gap-1 mt-2">
                    <div className="flex gap-2">
                      <Input placeholder="Add a comment…" value={newComment} onChange={(e) => { setNewComment(e.target.value); setErrors((prev) => ({ ...prev, comment: "" })); }} data-testid="task-comment-input" />
                      <Button onClick={addComment} size="sm" data-testid="task-comment-submit"><MessageSquare className="w-4 h-4" /></Button>
                    </div>
                    {errors.comment && <span className="text-xs text-red-500 block mt-1">{errors.comment}</span>}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">Activity</div>
                  <div className="space-y-1 text-xs">
                    {activity.map((a) => (
                      <div key={a.id} className="flex justify-between text-muted-foreground font-mono">
                        <span>{a.event} {a.details?.from ? `${a.details.from} → ${a.details.to}` : ""}</span>
                        <span>{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                    {activity.length === 0 && <div className="text-muted-foreground">No activity</div>}
                  </div>
                </div>
              </div>
              <DialogFooter>
                {hasPerm(me, "task.delete") && (
                  <Button variant="ghost" onClick={() => del(detailTask)} data-testid="task-detail-delete">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                )}
                <Button variant="outline" onClick={handleCloseDetail}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
